import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Security,
  Lock,
} from '@mui/icons-material';
import { AuthService } from '../services/authService';
import { MFAService } from '../services/mfaService';
import { useAuth } from '../contexts/AuthContext';


const LoginPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Sign In Form
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');

  // Sign Up Form
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Password Reset Form
  const [resetEmail, setResetEmail] = useState('');

  // MFA State
  const [mfaStep, setMfaStep] = useState(0); // 0: email/password, 1: MFA code
  const [mfaCode, setMfaCode] = useState('');
  const [tempUserCredential, setTempUserCredential] = useState<any>(null);

  // Check if user needs MFA based on logout status
  useEffect(() => {
    const checkAuthAndMFA = async () => {
      if (!currentUser) return;
      
      console.log('LoginPage - User authenticated:', currentUser.email);
      
      try {
        const idToken = await currentUser.getIdToken();
        const currentMfaStatus = await MFAService.getMFAStatus(currentUser.uid, idToken);
        console.log('MFA status:', currentMfaStatus);
        
        // Check if user was logged out explicitly (stored in Firestore)
        if (currentMfaStatus.explicit_logout) {
          console.log('User was logged out explicitly, checking MFA...');
          
          if (currentMfaStatus.enabled) {
            // MFA is enabled, show MFA verification step
            console.log('MFA enabled, showing MFA verification step');
            // MFA status stored in currentMfaStatus variable
            setTempUserCredential({ user: currentUser });
            setMfaStep(1);
            setSuccess('Please enter your 6-digit authentication code');
            // Don't clear the logout flag yet - wait for successful MFA verification
            return;
          }
        }
        
        // No MFA needed or user wasn't explicitly logged out, redirect to dashboard
        console.log('No MFA needed or user wasn\'t explicitly logged out, redirecting to dashboard');
        // Clear the logout flag in Firestore
        if (currentMfaStatus.explicit_logout) {
          await MFAService.clearLogoutFlag();
        }
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 500);
      } catch (error) {
        console.error('Error checking auth and MFA status:', error);
        // Fallback: redirect to dashboard
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 500);
      }
    };

    if (currentUser) {
      checkAuthAndMFA();
    }
  }, [currentUser]);

  // Helper function to format Firebase error messages
  const formatErrorMessage = (error: any): string => {
    const errorCode = error.code || '';
    const errorMessage = error.message || '';

    console.log('Firebase Error:', { code: errorCode, message: errorMessage, fullError: error });

    // Map Firebase error codes to user-friendly messages
    if (errorCode === 'auth/invalid-credential' || errorCode === 'auth/wrong-password' || errorCode === 'auth/user-not-found') {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (errorCode === 'auth/invalid-email') {
      return 'Invalid email address. Please enter a valid email.';
    }
    if (errorCode === 'auth/user-disabled') {
      return 'This account has been disabled. Please contact support.';
    }
    if (errorCode === 'auth/email-already-in-use') {
      return 'This email address is already registered. Please sign in instead.';
    }
    if (errorCode === 'auth/weak-password') {
      return 'Password is too weak. Please use at least 6 characters.';
    }
    if (errorCode === 'auth/password-does-not-meet-requirements') {
      return 'Password does not meet requirements. Please include: uppercase, lowercase, number, and special character (min 6 chars).';
    }
    if (errorCode === 'auth/network-request-failed') {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (errorCode === 'auth/too-many-requests') {
      return 'Too many failed attempts. Please try again later or reset your password.';
    }
    if (errorCode === 'auth/missing-password') {
      return 'Please enter a password.';
    }
    if (errorCode === 'auth/invalid-login-credentials') {
      return 'Invalid email or password. Please check your credentials and try again.';
    }

    // Check if the error message contains specific keywords
    if (errorMessage.toLowerCase().includes('invalid-credential') || 
        errorMessage.toLowerCase().includes('wrong-password') || 
        errorMessage.toLowerCase().includes('user-not-found') ||
        errorMessage.toLowerCase().includes('invalid_login_credentials')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (errorMessage.toLowerCase().includes('email-already-in-use') || 
        errorMessage.toLowerCase().includes('email_exists')) {
      return 'This email address is already registered. Please sign in instead.';
    }
    if (errorMessage.toLowerCase().includes('weak-password') || 
        errorMessage.toLowerCase().includes('password should be at least')) {
      return 'Password is too weak. Please use at least 6 characters.';
    }
    if (errorMessage.toLowerCase().includes('password') && errorMessage.toLowerCase().includes('requirements')) {
      return 'Password does not meet requirements. Please include: uppercase, lowercase, number, and special character (min 6 chars).';
    }
    if (errorMessage.toLowerCase().includes('too-many-requests')) {
      return 'Too many attempts. Please wait a moment and try again, or reset your password.';
    }
    if (errorMessage.toLowerCase().includes('network')) {
      return 'Connection error. Please check your internet and try again.';
    }
    if (errorMessage.toLowerCase().includes('invalid-email')) {
      return 'Please enter a valid email address.';
    }

    // Gentle fallback messages
    return 'Something went wrong. Please double-check your information and try again.';
  };


  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // First, authenticate with email and password
      const userCredential = await AuthService.signIn(signInEmail, signInPassword);
      
      // Check MFA status and logout flag from Firestore
      const idToken = await userCredential.user.getIdToken();
      const signInMfaStatus = await MFAService.getMFAStatus(userCredential.user.uid, idToken);
      console.log('Sign in - MFA status:', signInMfaStatus);
      
      // Check if user was logged out explicitly (stored in Firestore)
      if (signInMfaStatus.explicit_logout) {
        console.log('User was logged out explicitly, checking MFA...');
        
        if (signInMfaStatus.enabled) {
          // MFA is enabled - store temporary credential and show MFA step
          console.log('MFA is enabled, showing MFA step');
          // MFA status stored in signInMfaStatus variable
          setTempUserCredential(userCredential);
          setMfaStep(1);
          setSuccess('Please enter your 6-digit authentication code');
          // Don't clear the logout flag yet - wait for successful MFA verification
          return;
        }
      }
      
      // No MFA needed or user wasn't explicitly logged out, proceed with normal login
      console.log('No MFA needed or user wasn\'t explicitly logged out, proceeding with normal login');
      setSuccess('Successfully signed in!');
      setMfaStep(0);
      
      // Clear the logout flag in Firestore if it was set (for non-MFA users)
      if (signInMfaStatus.explicit_logout) {
        try {
          await MFAService.clearLogoutFlag();
          console.log('Logout flag cleared for non-MFA user');
        } catch (error) {
          console.error('Error clearing logout flag:', error);
        }
      }
      
      // Redirect to dashboard after successful login
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
    } catch (error: any) {
      setError(formatErrorMessage(error));
      setMfaStep(0);
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!tempUserCredential || !mfaCode) {
        throw new Error('Missing authentication data');
      }

      // Get the ID token for MFA verification
      const idToken = await tempUserCredential.user.getIdToken();

      // Verify the MFA code
      const isValid = await MFAService.verifyTOTPCode(tempUserCredential.user.uid, mfaCode, idToken);
      
      if (isValid) {
        // MFA verification successful - complete login
        setSuccess('Successfully signed in with MFA!');
        setMfaStep(0);
        setMfaCode('');
        // Clear MFA state
        setTempUserCredential(null);
        
        // Clear the logout flag after successful MFA verification
        try {
          await MFAService.clearLogoutFlag();
          console.log('Logout flag cleared after successful MFA verification');
        } catch (error) {
          console.error('Error clearing logout flag:', error);
        }
        
        // Redirect to dashboard after successful MFA
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1000);
      } else {
        setError('Invalid authentication code. Please try again.');
      }
    } catch (error: any) {
      setError(`MFA verification failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaStep(0);
    setMfaCode('');
    // Clear MFA state
    setTempUserCredential(null);
    setError('');
    setSuccess('');
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (signUpPassword !== confirmPassword) {
      setError('Passwords do not match. Please make sure both passwords are identical.');
      setLoading(false);
      return;
    }

    try {
      await AuthService.signUp(signUpEmail, signUpPassword);
      setSuccess('Account created successfully! Please check your email for verification.');
    } catch (error: any) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await AuthService.sendPasswordResetEmail(resetEmail);
      setSuccess('Password reset email sent! Check your inbox.');
    } catch (error: any) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 25%, #90caf9 50%, #e3f2fd 75%, #f5f5f5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.4,
        }
      }}
    >
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={8}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            background: 'white',
            width: '100%',
            maxWidth: 500,
          }}
        >
          {/* Header */}
          <Box sx={{ p: 3, textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <img 
                src="https://rctech.org/wp-content/uploads/2024/09/RCTECH-Logo-Empowering-Decisions-Blue.png" 
                alt="RCTECH Logo" 
                style={{ height: '40px', width: 'auto', marginRight: '12px' }}
              />
            </Box>
            <Typography variant="h5" component="h1" fontWeight="bold" color="#1976d2" sx={{ mb: 1 }}>
              Welcome to RCTECH Accelerators Portal
            </Typography>
            <Typography variant="body1" color="#42a5f5" sx={{ mb: 2, fontWeight: 500 }}>
              Empowering Decisions. Enhancing Value.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Please log in to access authorized accelerator modules.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Only registered users are permitted to execute operations.
            </Typography>
          </Box>

          {/* Main Login Form */}
          <Box sx={{ p: 3 }}>
            {/* Login Steps Stepper */}
            {mfaStep > 0 && (
              <Box sx={{ mb: 3 }}>
                <Stepper activeStep={mfaStep} alternativeLabel>
                  <Step>
                    <StepLabel>Email & Password</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>MFA Verification</StepLabel>
                  </Step>
                </Stepper>
              </Box>
            )}

            {/* Step 0: Email and Password */}
            {mfaStep === 0 && tabValue === 0 && (
              <form onSubmit={handleSignIn}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Email Address:*
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={signInEmail}
                    onChange={(e) => setSignInEmail(e.target.value)}
                    required
                    variant="outlined"
                    size="small"
                    placeholder="Enter your email address"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      }
                    }}
                  />
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Password:*
                  </Typography>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={signInPassword}
                    onChange={(e) => setSignInPassword(e.target.value)}
                    required
                    variant="outlined"
                    size="small"
                    placeholder="Enter your password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  startIcon={!loading && <Lock sx={{ fontSize: '1.1rem' }} />}
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>
                
                {/* Additional Options */}
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Don't have an account?
                  </Typography>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => setTabValue(1)}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      mb: 1
                    }}
                  >
                    Sign Up
                  </Button>
                </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => setTabValue(2)}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    Forgot your password?
                  </Button>
                </Box>
              </form>
            )}

            {/* Step 1: MFA Verification */}
            {mfaStep === 1 && (
              <form onSubmit={handleMFAVerification}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Security sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Multi-Factor Authentication
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Please enter the 6-digit code from your authenticator app
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    variant="outlined"
                    size="small"
                    placeholder="123456"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        letterSpacing: '0.5em'
                      }
                    }}
                    inputProps={{ maxLength: 6 }}
                  />
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || mfaCode.length !== 6}
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 2
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Verify & Sign In'}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={handleBackToLogin}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    ← Back to Login
                  </Button>
                </Box>
              </form>
            )}

            {tabValue === 1 && (
              <form onSubmit={handleSignUp}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Email Address:*
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={signUpEmail}
                    onChange={(e) => setSignUpEmail(e.target.value)}
                    required
                    variant="outlined"
                    size="small"
                    placeholder="Enter your email address"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      }
                    }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Password:*
                  </Typography>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={signUpPassword}
                    onChange={(e) => setSignUpPassword(e.target.value)}
                    required
                    variant="outlined"
                    size="small"
                    placeholder="Enter your password"
                    helperText="Must contain: uppercase, lowercase, number, special character (min 6 chars)"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                        marginTop: '4px'
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Confirm Password:*
                  </Typography>
                  <TextField
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    variant="outlined"
                    size="small"
                    placeholder="Confirm your password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            size="small"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 2
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Account'}
                </Button>
                
                {/* Back to Sign In */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Already have an account?
                  </Typography>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => setTabValue(0)}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    Sign In
                  </Button>
                </Box>
              </form>
            )}

            {tabValue === 2 && (
              <form onSubmit={handlePasswordReset}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Email Address:*
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    variant="outlined"
                    size="small"
                    placeholder="Enter your email address"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      }
                    }}
                  />
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 2
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Send Reset Email'}
                </Button>
                
                {/* Back to Sign In */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Remember your password?
                  </Typography>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => setTabValue(0)}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    Back to Sign In
                  </Button>
                </Box>
              </form>
            )}
          </Box>


          {error && (
            <Box sx={{ p: 2 }}>
              <Alert 
                severity="error" 
                onClose={() => setError('')}
                sx={{
                  backgroundColor: '#fff3f3',
                  color: '#d32f2f',
                  '& .MuiAlert-icon': {
                    color: '#d32f2f'
                  }
                }}
              >
                {error}
              </Alert>
            </Box>
          )}

          {success && (
            <Box sx={{ p: 2 }}>
              <Alert severity="success">{success}</Alert>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
              © 2025 RCTECH Solutions Inc. | Authorized Use Only
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }}>
                Privacy Policy
              </Typography>
              <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }}>
                Terms of Service
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
